import React, { Children, useLayoutEffect, useRef, useState } from 'react';
import {
    ConfigField,
    getMenuItems,
    ITEM_CONFIG,
    SortableSearchListItemProps,
    sortableSearchListItemStyles,
} from './sortable-search-list-item-helper';
import { Box, Grid, Typography } from '@mui/material';
import { ellipsifyTextInTwoLines } from 'lib';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Menu, MenuItem, Tooltip } from '@biq/apex-ui';
import {
    menuItemStyles,
    styles,
} from 'components/update-page-header/update-page-header-helper';
import { updatePipelineStyles as tooltipStyles } from 'pages/pipelines/CreateOrUpdatePipeline/create-or-update-pipeline-helper';

export const SortableSearchListItem = ({
    data,
    type,
    handleActionsClick,
    showItemMenu = true,
}: SortableSearchListItemProps): React.JSX.Element => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const containerRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const checkMultiline = (el: HTMLDivElement | null): boolean =>
            !!el && el.clientHeight > 21;

        const fieldMultiline = checkMultiline(containerRef.current);

        if (fieldMultiline) {
            containerRef.current!.style.alignItems = 'flex-start';
        } else {
            containerRef.current!.style.alignItems = 'center';
        }
    }, [containerRef.current]);

    const handleItemMenuClick = (
        event: React.MouseEvent<HTMLElement>
    ): void => {
        setAnchorEl(event.currentTarget);
    };

    const handleItemMenuClose = (): void => {
        setAnchorEl(null);
    };

    const handleClickMenuItem = (menuItem: any): void => {
        const dataWithAction = {
            action: menuItem.label.includes('Edit') ? 'edit' : 'delete',
            data,
        };
        handleActionsClick?.(dataWithAction);
        handleItemMenuClose();
    };

    const fields: ConfigField[] = ITEM_CONFIG[type];

    return (
        <>
            <Grid
                sx={{
                    ...sortableSearchListItemStyles.listItemContainerStyles,
                    minWidth: showItemMenu ? '850px' : '591px',
                }}
            >
                <Grid
                    ref={containerRef}
                    sx={{
                        ...sortableSearchListItemStyles.sortableItemContainerStyles,
                        width: showItemMenu ? 'calc(100% - 70px)' : '100%',
                    }}
                >
                    {Children.toArray(
                        fields.map(({ label, getValue }) => {
                            const value = getValue(data);
                            return (
                                <Grid
                                    key={label}
                                    sx={
                                        sortableSearchListItemStyles.sortableItemStyles
                                    }
                                    width={'48%'}
                                >
                                    <Grid
                                        sx={
                                            sortableSearchListItemStyles.sortableItemLabelStyles
                                        }
                                    >
                                        {label}:
                                    </Grid>
                                    <Tooltip
                                        arrow
                                        title={
                                            <Box
                                                sx={
                                                    tooltipStyles.tooltipOverflow
                                                }
                                            >
                                                {value}
                                            </Box>
                                        }
                                    >
                                        <Grid
                                            sx={{
                                                ...sortableSearchListItemStyles.sortableItemValueStyles,
                                                ...ellipsifyTextInTwoLines,
                                            }}
                                        >
                                            {value}
                                        </Grid>
                                    </Tooltip>
                                </Grid>
                            );
                        })
                    )}
                </Grid>
                {showItemMenu && (
                    <Grid>
                        <IconButton
                            onClick={handleItemMenuClick}
                            sx={{
                                ...styles.iconButtonStyles,
                                boxShadow: 'none',
                            }}
                        >
                            <MoreVertIcon />
                        </IconButton>
                    </Grid>
                )}
            </Grid>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleItemMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                sx={menuItemStyles('258px')}
            >
                {Children.toArray(
                    getMenuItems(type)?.map((item) => (
                        <Grid>
                            <MenuItem
                                onClick={(): void => handleClickMenuItem(item)}
                                sx={sortableSearchListItemStyles.menuItemHeight}
                            >
                                <Box
                                    sx={
                                        sortableSearchListItemStyles.menuItemContainer
                                    }
                                >
                                    {item.icon}
                                    <Typography
                                        sx={
                                            sortableSearchListItemStyles.menuItemTextStyles
                                        }
                                    >
                                        {item.label}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        </Grid>
                    ))
                )}
            </Menu>
        </>
    );
};
